import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Truck, MapPin, DollarSign, FileText, AlertTriangle, Fuel, Briefcase } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { vehicleService } from '../../services/vehicle.service';
import { tripService } from '../../services/trip.service';
import { allowanceService } from '../../services/allowance.service';
import { invoiceService } from '../../services/invoice.service';
import { fuelService } from '../../services/fuel.service';
import { contractService } from '../../services/contract.service';
import { TripStatus, AllowanceStatus, InvoiceStatus, UserRole, ContractStatus } from '../../types';
import { useAuthStore } from '../../store/auth.store';

interface MetricCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, color }) => (
  <Card className="hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{title}</p>
        <p className="text-3xl font-bold text-neutral-charcoal">{value}</p>
      </div>
      <div className={`p-3 rounded-full ${color}`}>
        {icon}
      </div>
    </div>
  </Card>
);

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasAnyRole, user } = useAuthStore();
  
  const isDriver = hasAnyRole([UserRole.DRIVER]);
  const canManageFleet = hasAnyRole([UserRole.ADMIN, UserRole.FLEET_MANAGER]);
  const canManageFinance = hasAnyRole([UserRole.ADMIN, UserRole.FINANCE_OFFICER]);
  const driverId = user?.driverId;

  const { data: vehicles } = useQuery({
    queryKey: ['vehicles'],
    queryFn: vehicleService.getActive,
    enabled: canManageFleet,
  });

  const { data: allTrips } = useQuery({
    queryKey: isDriver && driverId ? ['trips', 'driver', driverId] : ['trips'],
    queryFn: () => isDriver && driverId ? tripService.getByDriver(driverId) : tripService.getAll(),
    enabled: !isDriver || !!driverId,
  });

  const { data: pendingAllowances } = useQuery({
    queryKey: ['allowances', AllowanceStatus.PENDING],
    queryFn: () => allowanceService.getByStatus(AllowanceStatus.PENDING),
    enabled: canManageFinance,
  });

  const { data: allInvoices } = useQuery({
    queryKey: ['invoices'],
    queryFn: invoiceService.getAll,
    enabled: canManageFinance,
  });

  const { data: flaggedFuel } = useQuery({
    queryKey: ['fuel', 'flagged'],
    queryFn: fuelService.getFlagged,
  });

  const { data: contracts } = useQuery({
    queryKey: ['contracts'],
    queryFn: contractService.getAll,
    enabled: canManageFleet || canManageFinance,
  });

  const activeTripsToday = allTrips?.filter(
    (trip) => trip.status === TripStatus.ACTIVE
  ).length || 0;

  const draftInvoices = allInvoices?.filter(
    (invoice) => invoice.status === InvoiceStatus.DRAFT
  ).length || 0;

  const activeContracts = contracts?.filter(
    (c) => c.status === ContractStatus.ACTIVE || c.status === ContractStatus.IN_PROGRESS
  ).length || 0;

  const recentTrips = allTrips?.slice(0, 5) || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-charcoal mb-2">
          {isDriver ? 'Driver Dashboard' : 'Dashboard'}
        </h1>
        <p className="text-gray-600">
          {isDriver ? 'Overview of your trips and activities' : 'Overview of your transport operations'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {(canManageFleet || canManageFinance) && (
          <MetricCard
            title="Active Contracts"
            value={activeContracts}
            icon={<Briefcase className="text-white" size={24} />}
            color="bg-blue-600"
          />
        )}
        {canManageFleet && (
          <MetricCard
            title="Active Vehicles"
            value={vehicles?.length || 0}
            icon={<Truck className="text-white" size={24} />}
            color="bg-primary"
          />
        )}
        <MetricCard
          title={isDriver ? "My Active Trips" : "Active Trips Today"}
          value={activeTripsToday}
          icon={<MapPin className="text-white" size={24} />}
          color="bg-success"
        />
        {canManageFinance && (
          <>
            <MetricCard
              title="Pending Allowances"
              value={pendingAllowances?.length || 0}
              icon={<DollarSign className="text-white" size={24} />}
              color="bg-warning"
            />
            <MetricCard
              title="Draft Invoices"
              value={draftInvoices}
              icon={<FileText className="text-white" size={24} />}
              color="bg-secondary-steel"
            />
          </>
        )}
        <MetricCard
          title="Flagged Fuel Records"
          value={flaggedFuel?.length || 0}
          icon={<AlertTriangle className="text-white" size={24} />}
          color="bg-error"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title={isDriver ? "My Recent Trips" : "Recent Trips"}>
          <div className="space-y-3">
            {recentTrips.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No trips found</p>
            ) : (
              recentTrips.map((trip) => (
                <div
                  key={trip.id}
                  className="flex items-center justify-between p-3 bg-neutral-light rounded-lg"
                >
                  <div>
                    <p className="font-medium text-neutral-charcoal">{trip.tripNumber}</p>
                    <p className="text-sm text-gray-500">
                      {trip.origin} → {trip.destination}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded ${
                      trip.status === TripStatus.ACTIVE
                        ? 'bg-green-100 text-green-800'
                        : trip.status === TripStatus.COMPLETED
                        ? 'bg-gray-100 text-gray-800'
                        : trip.status === TripStatus.PLANNED
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {trip.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </Card>

        <Card title="Quick Actions">
          <div className="grid grid-cols-2 gap-3">
            {canManageFleet && (
              <button 
                onClick={() => navigate('/trips/create')}
                className="p-4 bg-primary text-white rounded-lg hover:bg-primary-navy transition-colors"
              >
                <MapPin className="mx-auto mb-2" size={24} />
                <p className="text-sm font-medium">Create Trip</p>
              </button>
            )}
            <button 
              onClick={() => navigate('/fuel')}
              className="p-4 bg-accent-orange text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <Fuel className="mx-auto mb-2" size={24} />
              <p className="text-sm font-medium">Record Fuel</p>
            </button>
            {canManageFleet && (
              <button 
                onClick={() => navigate('/fleet/vehicles')}
                className="p-4 bg-success text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                <Truck className="mx-auto mb-2" size={24} />
                <p className="text-sm font-medium">Manage Fleet</p>
              </button>
            )}
            {canManageFinance && (
              <button 
                onClick={() => navigate('/finance/invoices')}
                className="p-4 bg-secondary-steel text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                <FileText className="mx-auto mb-2" size={24} />
                <p className="text-sm font-medium">Generate Invoice</p>
              </button>
            )}
            <button 
              onClick={() => navigate('/trips')}
              className="p-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <MapPin className="mx-auto mb-2" size={24} />
              <p className="text-sm font-medium">{isDriver ? 'My Trips' : 'View Trips'}</p>
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
};
